import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Crown, Users, Edit2, Save, X, AlertTriangle } from 'lucide-react';
import { User, Team } from '@/types';
import { apiService } from '@/services/api';

interface SupervisorManagementProps {
  teams: Team[];
  allUsers: User[];
  onRefresh: () => void;
  isLoading?: boolean;
}

interface TeamAssignment {
  teamId: number;
  teamName: string;
  currentSupervisorId: number;
  currentSupervisorName: string;
  newSupervisorId?: number;
  newSupervisorName?: string;
}

const SupervisorManagement: React.FC<SupervisorManagementProps> = ({
  teams,
  allUsers,
  onRefresh,
  isLoading
}) => {
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, number>>(new Map());
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    changes: TeamAssignment[];
  }>({ isOpen: false, changes: [] });
  const [isSaving, setIsSaving] = useState(false);

  // Get all supervisors from users
  const supervisors = useMemo(() => 
    allUsers.filter(user => user.role === 'supervisor'),
    [allUsers]
  );

  // Get team assignments with current and pending supervisor info
  const teamAssignments = useMemo<TeamAssignment[]>(() => {
    return teams.map(team => {
      const currentSupervisor = allUsers.find(user => user.id === team.supervisorId);
      const pendingId = pendingChanges.get(team.id);
      const newSupervisor = pendingId ? allUsers.find(user => user.id === pendingId) : undefined;

      return {
        teamId: team.id,
        teamName: team.name,
        currentSupervisorId: team.supervisorId,
        currentSupervisorName: currentSupervisor?.name || 'Fără supervisor',
        newSupervisorId: pendingId,
        newSupervisorName: newSupervisor?.name
      };
    });
  }, [teams, allUsers, pendingChanges]);

  // Get unassigned supervisors (not assigned to any team)
  const unassignedSupervisors = useMemo(() => {
    const assignedSupervisorIds = teams.map(team => team.supervisorId);
    return supervisors.filter(supervisor => !assignedSupervisorIds.includes(supervisor.id));
  }, [supervisors, teams]);

  const handleSupervisorChange = (teamId: number, newSupervisorId: number) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      if (newSupervisorId === teams.find(t => t.id === teamId)?.supervisorId) {
        // If selecting the current supervisor, remove the pending change
        updated.delete(teamId);
      } else {
        updated.set(teamId, newSupervisorId);
      }
      return updated;
    });
  };

  const discardChanges = () => {
    setPendingChanges(new Map());
    setEditingTeamId(null);
  };

  const confirmChanges = () => {
    const changes = teamAssignments.filter(assignment => 
      assignment.newSupervisorId && assignment.newSupervisorId !== assignment.currentSupervisorId
    );

    if (changes.length === 0) {
      discardChanges();
      return;
    }

    setConfirmDialog({ isOpen: true, changes });
  };

  const applyChanges = async () => {
    if (confirmDialog.changes.length === 0) return;

    setIsSaving(true);
    try {
      // Apply all changes in parallel
      await Promise.all(
        confirmDialog.changes.map(change => {
          const team = teams.find(t => t.id === change.teamId);
          if (!change.newSupervisorId || !team?.monitorId) {
            throw new Error(`Invalid data for team ${change.teamName}`);
          }
          return apiService.updateTeam(change.teamId, {
            name: change.teamName,
            supervisorId: change.newSupervisorId,
            monitorId: team.monitorId
          });
        })
      );

      // Refresh data and clear pending changes
      onRefresh();
      setPendingChanges(new Map());
      setEditingTeamId(null);
      setConfirmDialog({ isOpen: false, changes: [] });
    } catch (error) {
      console.error('Error updating team supervisors:', error);
      // You could add a toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  const getSupervisorTeamCount = (supervisorId: number) => {
    return teams.filter(team => team.supervisorId === supervisorId).length;
  };

  const hasChanges = pendingChanges.size > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-orange-600" />
          <h2 className="text-2xl font-bold">Management Supervizori</h2>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <>
              <Button 
                onClick={discardChanges} 
                variant="outline" 
                size="sm"
                disabled={isSaving}
              >
                <X className="w-4 h-4 mr-1" />
                Anulează
              </Button>
              <Button 
                onClick={confirmChanges} 
                size="sm"
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-1" />
                Salvează modificările ({pendingChanges.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Team Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Asignări Echipe ({teams.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {teamAssignments.map(assignment => {
              const hasChange = assignment.newSupervisorId !== undefined;
              const isEditing = editingTeamId === assignment.teamId;

              return (
                <div 
                  key={assignment.teamId}
                  className={`p-4 rounded-lg border transition-colors ${
                    hasChange ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-2">{assignment.teamName}</h4>
                      
                      {isEditing ? (
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">Supervisor:</span>
                          <Select
                            value={assignment.newSupervisorId?.toString() || assignment.currentSupervisorId.toString()}
                            onValueChange={(value) => handleSupervisorChange(assignment.teamId, parseInt(value))}
                          >
                            <SelectTrigger className="w-64">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {supervisors.map(supervisor => (
                                <SelectItem key={supervisor.id} value={supervisor.id.toString()}>
                                  <div className="flex items-center justify-between w-full">
                                    <span>{supervisor.name}</span>
                                    <Badge variant="outline" className="ml-2 text-xs">
                                      {getSupervisorTeamCount(supervisor.id)} echipe
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">Supervisor curent:</span>
                          <Badge variant="outline">
                            {assignment.currentSupervisorName}
                          </Badge>
                          {hasChange && (
                            <>
                              <span className="text-sm text-blue-600">→</span>
                              <Badge variant="default">
                                {assignment.newSupervisorName}
                              </Badge>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {hasChange && !isEditing && (
                        <Badge variant="secondary" className="text-xs">
                          Modificat
                        </Badge>
                      )}
                      <Button
                        onClick={() => setEditingTeamId(isEditing ? null : assignment.teamId)}
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        {isEditing ? 'Gata' : 'Editează'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Unassigned Supervisors */}
      {unassignedSupervisors.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Supervizori Neasignați ({unassignedSupervisors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {unassignedSupervisors.map(supervisor => (
                <div key={supervisor.id} className="p-3 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">{supervisor.name}</span>
                    <Badge variant="outline" className="ml-auto text-xs">
                      Neasignat
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => 
        !open && setConfirmDialog({ isOpen: false, changes: [] })
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați modificările</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  Sunteți sigur că doriți să aplicați următoarele modificări?
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {confirmDialog.changes.map(change => (
                    <div key={change.teamId} className="p-2 bg-gray-50 rounded text-sm">
                      <strong>{change.teamName}</strong>: {change.currentSupervisorName} → {change.newSupervisorName}
                    </div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>
              Anulează
            </AlertDialogCancel>
            <AlertDialogAction onClick={applyChanges} disabled={isSaving}>
              {isSaving ? 'Se salvează...' : 'Confirmă modificările'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupervisorManagement;